import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {Dialog, DialogActions, DialogContent, Grid, Paper} from '@mui/material';
import Button from '@mui/material/Button';
import {Formik} from 'formik';
import {useAppTranslation} from 'services/i18n';
import {DataGridColFilter} from 'components/DataGrid';
import {TextFormField} from 'components/form/TextFormField';
import {SelectFormField} from 'components/form/SelectFormField';
import {UserSelectFormField} from 'components/form/UserSelectFormField';
import {defaultDialogStyle} from 'utils/dialog';
import {APPROVAL_REQUEST_STATUSES, APPROVAL_REQUEST_TYPES} from 'model/approval';
import {ApprovalFilterType, ApprovalGridFilter} from 'pages/ApprovalsPage';

const dialogStyle = defaultDialogStyle(600);

export const ApprovalFilterDialog = ({colFilter, onClose}: {
    colFilter: DataGridColFilter<ApprovalGridFilter, ApprovalFilterType>,
    onClose: () => void
}) => {
    const t = useAppTranslation();

    const filterName = colFilter.filterName as keyof ApprovalGridFilter;

    const initialValues: ApprovalGridFilter = useMemo(() => ({
        [filterName]: colFilter.currentFilter[filterName]
    }), [colFilter.currentFilter, filterName]);

    const handleSave = useCallback(async (values: ApprovalGridFilter) => {
        // undefined first so a cleared field overrides the previous value
        colFilter.onApply({[colFilter.filterName]: undefined, ...values});
        onClose();
    }, [colFilter, onClose]);

    const handleReset = useCallback(async () => {
        const defaultValue = colFilter.defaultFilter[filterName];
        await handleSave({
            [filterName]: defaultValue === undefined
                ? defaultValue
                : JSON.parse(JSON.stringify(defaultValue))
        });
    }, [filterName, colFilter.defaultFilter, handleSave]);

    const typeOptions = useMemo(() => APPROVAL_REQUEST_TYPES.map(value => ({
        value, label: t('approvals.type.' + value)
    })), [t]);

    const statusOptions = useMemo(() => APPROVAL_REQUEST_STATUSES.map(value => ({
        value, label: t('approvals.status.' + value)
    })), [t]);

    const isFilterUsed = useMemo(() =>
        JSON.stringify(colFilter.currentFilter[filterName]) !== JSON.stringify(colFilter.defaultFilter[filterName])
    , [filterName, colFilter.currentFilter, colFilter.defaultFilter]);

    return <Dialog open={true} onClose={onClose} PaperComponent={Paper} sx={dialogStyle}>
        <Formik initialValues={initialValues} onSubmit={handleSave}>
            {({handleSubmit}) => <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={1} sx={{minHeight: '100px'}} className={'tw-mt-8 tw-pt-8'}>
                        <Grid size={12}>
                            {filterName === 'filterUserIds'
                                && <UserSelectFormField name={filterName}
                                    label={t('approvals.filter.user.label')}
                                    placeholder={t('approvals.filter.user.placeholder')}/>}
                            {filterName === 'provider'
                                && <TextFormField name={filterName}
                                    label={t('approvals.filter.provider.label')}
                                    placeholder={t('approvals.filter.provider.placeholder')}
                                    maxlength={100}/>}
                            {filterName === 'filterRequestTypes'
                                && <SelectFormField name={filterName} isMulti clearable
                                    options={typeOptions}
                                    label={t('approvals.filter.type.label')}
                                    placeholder={t('approvals.filter.type.placeholder')}/>}
                            {filterName === 'filterStatuses'
                                && <SelectFormField name={filterName} isMulti clearable
                                    options={statusOptions}
                                    label={t('approvals.filter.status.label')}
                                    placeholder={t('approvals.filter.status.placeholder')}/>}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{padding: '16px'}}>
                    <Button color={'inherit'} onClick={onClose}>{t('form.cancel')}</Button>
                    {isFilterUsed && <Button color={'secondary'} onClick={handleReset}>{t('userGroup.filter.cancel')}</Button>}
                    <Button color={'primary'} variant={'contained'} type={'submit'}>{t('userGroup.filter.apply')}</Button>
                </DialogActions>
            </form>}
        </Formik>
    </Dialog>;
};
